<template>
  <!-- Desktop Modal Layout -->
  <div v-if="isOpen && !isMobile" class="fixed inset-0 z-[999999] flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white p-4 md:p-8 rounded-lg max-w-5xl w-11/12 max-h-[90vh] overflow-y-auto relative modal-content">
      <div class="absolute top-4 right-40">
        <StarButton
          v-if="content.slug"
          :item-id="content.slug"
          item-type="place"
          :item-data="{ name: content.name, country: content.country }"
          size="medium"
          variant="default"
        />
      </div>
      <button
          @click="goToQuiz"
          class="absolute top-4 right-28 text-white font-bold text-lg w-10 h-10 rounded-full flex items-center justify-center shadow-lg gradient-quiz-btn"
          aria-label="Take Quiz"
          title="Take Quiz"
      >
        <i class="fas fa-graduation-cap"></i>
      </button>
      <button
          @click="sharePlace"
          class="absolute top-4 right-16 text-white font-bold text-lg w-10 h-10 rounded-full flex items-center justify-center shadow-lg gradient-share-btn"
          aria-label="Share Place"
      >
        <i class="fas fa-share-alt"></i>
      </button>
      <button
          @click="closeModal"
          class="absolute top-4 right-4 text-white font-bold text-lg w-10 h-10 rounded-full flex items-center justify-center shadow-lg gradient-close-btn"
          aria-label="Close Modal"
      >
        <i class="fas fa-times"></i>
      </button>
      <!-- Enhanced Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="flag-section">
            <img :src="flagUrl" alt="Country flag" class="header-flag">
          </div>
          <div class="place-info">
            <h2 class="place-title">{{ content.name }}</h2>
            <p class="place-location">
              <i class="fas fa-map-marker-alt"></i>
              {{ content.territorial_divisions?.region || '' }}{{ content.territorial_divisions?.region && content.country ? ', ' : '' }}{{ content.country || '' }}
            </p>
          </div>
        </div>
        <div class="description-section">
          <p class="place-description">{{ content.description }}</p>
        </div>
      </div>
      
      <!-- Share Card for Desktop -->
      <div v-if="showShareCard" class="modal-share-card">
        <div class="modal-share-card-header">
          <i class="fas fa-link"></i>
          <span>Share this place</span>
          <button @click="closeShareCard" class="modal-share-close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-share-url-container">
          <input ref="shareUrlInput" :value="shareUrl" readonly class="modal-share-url-input">
          <button @click="copyUrl" class="modal-copy-btn" :class="{ 'copied': urlCopied }">
            <i :class="urlCopied ? 'fas fa-check' : 'fas fa-copy'"></i>
            {{ urlCopied ? 'Copied!' : 'Copy' }}
          </button>
        </div>
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- History -->
        <div class="col-span-full">
          <h3 class="section-header">
            <i class="fas fa-scroll"></i>
            <span>History</span>
          </h3>
          <div class="info-card p-6 rounded-xl shadow-sm mb-6">
            <p>{{ content.history }}</p>
          </div>
        </div>
        <!-- Geography & Demographics -->
        <div class="col-span-full">
          <h3 class="section-header">
            <i class="fas fa-globe-americas"></i>
            <span>Geography & Demographics</span>
          </h3>
          
          <!-- Stats Card -->
          <div class="info-card p-6 rounded-xl shadow-sm mb-4">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div v-if="content.country_population" class="stat-item">
                <i class="fas fa-users stat-icon"></i>
                <div class="stat-content">
                  <span class="stat-label">Country Population</span>
                  <span class="stat-value">{{ content.country_population }}</span>
                </div>
              </div>
              <div v-if="content.country_extension" class="stat-item">
                <i class="fas fa-map stat-icon"></i>
                <div class="stat-content">
                  <span class="stat-label">Country Area</span>
                  <span class="stat-value">{{ content.country_extension }}</span>
                </div>
              </div>
              <div v-if="content.division_population" class="stat-item">
                <i class="fas fa-user-friends stat-icon"></i>
                <div class="stat-content">
                  <span class="stat-label">Local Population</span>
                  <span class="stat-value">{{ content.division_population }}</span>
                </div>
              </div>
              <div v-if="content.division_extension" class="stat-item">
                <i class="fas fa-expand-arrows-alt stat-icon"></i>
                <div class="stat-content">
                  <span class="stat-label">Local Area</span>
                  <span class="stat-value">{{ content.division_extension }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Languages Row -->
          <div v-if="content.languages_spoken && content.languages_spoken.length" class="info-card p-4 rounded-xl shadow-sm mb-4">
            <div class="flex items-center gap-3 mb-3">
              <i class="fas fa-language text-blue-600"></i>
              <span class="font-semibold text-gray-800">Languages:</span>
            </div>
            <div class="flex flex-wrap gap-2">
              <span v-for="language in content.languages_spoken" :key="language.iso_639_1 || language.name" 
                    class="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200">
                {{ language.name }}
                <span v-if="language.approx_percentage" class="text-xs opacity-75">({{ language.approx_percentage }})</span>
                <span class="language-status-indicator" :class="language.official_status"></span>
              </span>
            </div>
          </div>
          
          <!-- Geography Description Card -->
          <div class="info-card p-6 rounded-xl shadow-sm mb-6">
            <p>{{ content.geography }}</p>
          </div>
        </div>
        <!-- Landmarks -->
        <div v-if="content.landmarks && content.landmarks.length" class="col-span-1">
          <h3 class="section-header">
            <i class="fas fa-monument"></i>
            <span>Landmarks</span>
          </h3>
          <div v-for="landmark in filteredLandmarks" :key="landmark.name" class="info-card p-6 rounded-xl shadow-sm mb-6">
            <div class="flex gap-4">
              <img v-if="landmark.thumbnail" :src="landmark.thumbnail" :alt="landmark.name" class="item-thumbnail" @click="openImagesModal(landmark.name, landmark.slug)" title="Click to view images">
              <div class="flex-1">
                <h4 class="text-xl font-bold">{{ landmark.name }}</h4>
                <p>{{ landmark.description }}</p>
              </div>
            </div>
            <div class="flex justify-end mt-2 space-x-2">
              <a
                  :href="landmark.wikipedia_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="action-btn-wiki"
                  aria-label="Wikipedia"
              >
                <i class="fab fa-wikipedia-w"></i>
              </a>
              <button
                  @click="openImagesModal(landmark.name, landmark.slug)"
                  class="action-btn-primary"
              >
                <i class="fas fa-camera"></i>
                View Images
              </button>
              <button
                  @click="openMoreInfoModal(landmark.name, landmark.slug)"
                  class="action-btn-secondary"
              >
                <i class="fas fa-info-circle"></i>
                More Information
              </button>
            </div>
          </div>
        </div>
        <!-- Famous Places -->
        <div v-if="content.famous_places && content.famous_places.length" class="col-span-1">
          <h3 class="section-header">
            <i class="fas fa-star"></i>
            <span>Famous Places</span>
          </h3>
          <div v-for="place in filteredFamousPlaces" :key="place.name" class="info-card p-6 rounded-xl shadow-sm mb-6">
            <div class="flex gap-4">
              <img v-if="place.thumbnail" :src="place.thumbnail" :alt="place.name" class="item-thumbnail" @click="openImagesModal(place.name, place.slug)" title="Click to view images">
              <div class="flex-1">
                <h4 class="text-xl font-bold">{{ place.name }}</h4>
                <p>{{ place.description }}</p>
              </div>
            </div>
            <div class="flex justify-end mt-2 space-x-2">
              <a
                  :href="place.wikipedia_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="action-btn-wiki"
                  aria-label="Wikipedia"
              >
                <i class="fab fa-wikipedia-w"></i>
              </a>
              <button
                  @click="openImagesModal(place.name, place.slug)"
                  class="action-btn-primary"
              >
                <i class="fas fa-camera"></i>
                View Images
              </button>
              <button
                  @click="openMoreInfoModal(place.name, place.slug)"
                  class="action-btn-secondary"
              >
                <i class="fas fa-info-circle"></i>
                More Information
              </button>
            </div>
          </div>
        </div>
        <!-- Native Species -->
        <div v-if="content.native_species && content.native_species.length">
          <h3 class="section-header">
            <i class="fas fa-paw"></i>
            <span>Native Species</span>
          </h3>
          <div v-for="animal in filteredNativeSpecies" :key="animal.name" class="info-card p-6 rounded-xl shadow-sm mb-6">
            <h4 class="text-xl font-bold">{{ animal.name }}</h4>
            <p>{{ animal.description }}</p>
            <div class="flex justify-end mt-2 space-x-2">
              <a
                  :href="animal.wikipedia_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="action-btn-wiki"
                  aria-label="Wikipedia"
              >
                <i class="fab fa-wikipedia-w"></i>
              </a>
              <button
                  @click="openImagesModal(animal.name, animal.slug)"
                  class="action-btn-primary"
              >
                <i class="fas fa-camera"></i>
                View Images
              </button>
              <button
                  @click="openMoreInfoModal('Animal ' + animal.name, animal.slug)"
                  class="action-btn-secondary"
              >
                <i class="fas fa-info-circle"></i>
                More Information
              </button>
            </div>
          </div>
        </div>
        <!-- Traditional Foods -->
        <div v-if="content.traditional_foods && content.traditional_foods.length">
          <h3 class="section-header">
            <i class="fas fa-utensils"></i>
            <span>Traditional Foods</span>
          </h3>
          <div v-for="food in filteredTraditionalFoods" :key="food.name" class="info-card p-6 rounded-xl shadow-sm mb-6">
            <h4 class="text-xl font-bold">{{ food.name }}</h4>
            <p>{{ food.description }}</p>
            <div class="flex justify-end mt-2 space-x-2">
              <a
                  :href="food.wikipedia_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="action-btn-wiki"
                  aria-label="Wikipedia"
              >
                <i class="fab fa-wikipedia-w"></i>
              </a>
              <button
                  @click="openImagesModal(food.name, food.slug)"
                  class="action-btn-primary"
              >
                <i class="fas fa-camera"></i>
                View Images
              </button>
              <button
                  @click="openMoreInfoModal('Food ' + food.name, food.slug)"
                  class="action-btn-secondary"
              >
                <i class="fas fa-info-circle"></i>
                More Information
              </button>
            </div>
          </div>
        </div>
        <!-- Famous People -->
        <div v-if="content.famous_people && content.famous_people.length">
          <h3 class="section-header">
            <i class="fas fa-users"></i>
            <span>Famous People</span>
          </h3>
          <div v-for="person in filteredFamousPeople" :key="person.name" class="info-card p-6 rounded-xl shadow-sm mb-6">
            <h4 class="text-xl font-bold">{{ person.name }}</h4>
            <p>{{ person.description }}</p>
            <div class="flex justify-end mt-2 space-x-2">
              <a
                  :href="person.wikipedia_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="action-btn-wiki"
                  aria-label="Wikipedia"
              >
                <i class="fab fa-wikipedia-w"></i>
              </a>
              <button
                  @click="openImagesModal(person.name, person.slug)"
                  class="action-btn-primary"
              >
                <i class="fas fa-camera"></i>
                View Images
              </button>
              <button
                  @click="openMoreInfoModal(person.name, person.slug)"
                  class="action-btn-secondary"
              >
                <i class="fas fa-info-circle"></i>
                More Information
              </button>
            </div>
          </div>
        </div>
        
        
        <!-- Important Dates -->
        <div v-if="content.important_dates && content.important_dates.length" class="col-span-full">
          <h3 class="section-header">
            <i class="fas fa-calendar-alt"></i>
            <span>Important Dates & Celebrations</span>
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div v-for="date in content.important_dates" :key="date.month_day + date.name" class="info-card p-6 rounded-xl shadow-sm">
              <div class="flex items-start gap-4">
                <div class="date-badge">
                  <span class="date-text">{{ formatDateBadge(date.month_day) }}</span>
                </div>
                <div class="flex-1">
                  <div class="flex items-start justify-between mb-2">
                    <h4 class="text-lg font-bold text-gray-800">{{ date.name }}</h4>
                    <span class="scope-badge" :class="date.scope">
                      {{ date.scope }}
                    </span>
                  </div>
                  <p class="text-sm text-gray-700 mb-3 leading-relaxed">{{ date.description }}</p>
                  <div class="flex items-center justify-between text-xs text-gray-500">
                    <div class="flex items-center gap-2">
                      <span v-if="date.first_year">Since {{ date.first_year }}</span>
                    </div>
                    <a v-if="date.wikipedia_url" :href="date.wikipedia_url" target="_blank" rel="noopener noreferrer" class="action-btn-wiki-sm">
                      <i class="fab fa-wikipedia-w"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  </div>
  
  <!-- Mobile Drawer Layout -->
  <div v-if="isOpen && isMobile" class="mobile-drawer-overlay" :class="{ 'drawer-minimized': drawerMinimized }">
    <!-- Minimized Header Bar (always visible) -->
    <div class="drawer-header-bar" @click="expandDrawer" v-if="drawerMinimized">
      <div class="drawer-header-content">
        <img :src="flagUrl" alt="Country flag" class="drawer-mini-flag">
        <div class="drawer-mini-info">
          <h3 class="drawer-mini-title">{{ content.name }}</h3>
          <p class="drawer-mini-location">{{ content.territorial_divisions?.region || '' }}{{ content.territorial_divisions?.region && content.country ? ', ' : '' }}{{ content.country || '' }}</p>
        </div>
        <i class="fas fa-chevron-up drawer-expand-icon"></i>
      </div>
    </div>
    
    <!-- Full Drawer Content -->
    <div class="drawer-content" v-if="!drawerMinimized">
      <!-- Drawer Header with Controls -->
      <div class="drawer-header">
        <div class="drawer-controls">
          <button @click="minimizeDrawer" class="drawer-minimize-btn" aria-label="Minimize">
            <i class="fas fa-chevron-down"></i>
          </button>
          <div class="drawer-right-controls">
            <StarButton
              v-if="content.slug"
              :item-id="content.slug"
              item-type="place"
              :item-data="{ name: content.name, country: content.country }"
              size="small"
              variant="compact"
              class="drawer-star-btn"
            />
            <button @click="goToQuiz" class="drawer-quiz-btn gradient-quiz-btn" aria-label="Take Quiz" title="Take Quiz">
              <i class="fas fa-graduation-cap"></i>
            </button>
            <button @click="sharePlace" class="drawer-share-btn gradient-share-btn" aria-label="Share Place">
              <i class="fas fa-share-alt"></i>
            </button>
            <button @click="closeModal" class="drawer-close-btn gradient-close-btn" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        
        <!-- Enhanced Header -->
        <div class="drawer-modal-header">
          <div class="drawer-header-content-full">
            <div class="drawer-flag-section">
              <img :src="flagUrl" alt="Country flag" class="drawer-header-flag">
            </div>
            <div class="drawer-place-info">
              <h2 class="drawer-place-title">{{ content.name }}</h2>
              <p class="drawer-place-location">
                <i class="fas fa-map-marker-alt"></i>
                {{ content.territorial_divisions?.region || '' }}{{ content.territorial_divisions?.region && content.country ? ', ' : '' }}{{ content.country || '' }}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Scrollable Content -->
      <div class="drawer-scrollable-content">
        <div class="drawer-content-grid">
          <!-- Description -->
          <div class="drawer-section">
            <div class="drawer-description-card">
              <p class="drawer-place-description">{{ content.description }}</p>
            </div>
          </div>
          
          
          <!-- History -->
          <div class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-scroll"></i>
              <span>History</span>
            </h3>
            <div class="drawer-info-card">
              <p>{{ content.history }}</p>
            </div>
          </div>
          <!-- Geography & Demographics -->
          <div class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-globe-americas"></i>
              <span>Geography & Demographics</span>
            </h3>
            
            <!-- Stats Grid -->
            <div class="drawer-stats-grid">
              <div v-if="content.country_population" class="drawer-stat-item">
                <i class="fas fa-users drawer-stat-icon"></i>
                <div class="drawer-stat-content">
                  <span class="drawer-stat-label">Country Pop.</span>
                  <span class="drawer-stat-value">{{ content.country_population }}</span>
                </div>
              </div>
              <div v-if="content.country_extension" class="drawer-stat-item">
                <i class="fas fa-map drawer-stat-icon"></i>
                <div class="drawer-stat-content">
                  <span class="drawer-stat-label">Country Area</span>
                  <span class="drawer-stat-value">{{ content.country_extension }}</span>
                </div>
              </div>
              <div v-if="content.division_population" class="drawer-stat-item">
                <i class="fas fa-user-friends drawer-stat-icon"></i>
                <div class="drawer-stat-content">
                  <span class="drawer-stat-label">Local Pop.</span>
                  <span class="drawer-stat-value">{{ content.division_population }}</span>
                </div>
              </div>
              <div v-if="content.division_extension" class="drawer-stat-item">
                <i class="fas fa-expand-arrows-alt drawer-stat-icon"></i>
                <div class="drawer-stat-content">
                  <span class="drawer-stat-label">Local Area</span>
                  <span class="drawer-stat-value">{{ content.division_extension }}</span>
                </div>
              </div>
            </div>
            
            <!-- Geography Description -->
            <div class="drawer-info-card">
              <p>{{ content.geography }}</p>
            </div>
          </div>
          <!-- Landmarks -->
          <div v-if="content.landmarks && content.landmarks.length" class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-monument"></i>
              <span>Landmarks</span>
            </h3>
            <div v-for="landmark in filteredLandmarks" :key="landmark.name" class="drawer-info-card drawer-item-card">
              <div class="flex gap-3">
                <img v-if="landmark.thumbnail" :src="landmark.thumbnail" :alt="landmark.name" class="drawer-item-thumbnail" @click="openImagesModal(landmark.name, landmark.slug)" title="Click to view images">
                <div class="flex-1">
                  <h4 class="drawer-item-title">{{ landmark.name }}</h4>
                  <p>{{ landmark.description }}</p>
                </div>
              </div>
              <div class="drawer-action-buttons">
                <a :href="landmark.wikipedia_url" target="_blank" rel="noopener noreferrer" class="drawer-action-btn-wiki" aria-label="Wikipedia">
                  <i class="fab fa-wikipedia-w"></i>
                </a>
                <button @click="openImagesModal(landmark.name, landmark.slug)" class="drawer-action-btn-primary">
                  <i class="fas fa-camera"></i>
                  <span>Images</span>
                </button>
                <button @click="openMoreInfoModal(landmark.name, landmark.slug)" class="drawer-action-btn-secondary">
                  <i class="fas fa-info-circle"></i>
                  <span>More</span>
                </button>
              </div>
            </div>
          </div>
          <!-- Famous Places -->
          <div v-if="content.famous_places && content.famous_places.length" class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-star"></i>
              <span>Famous Places</span>
            </h3>
            <div v-for="place in filteredFamousPlaces" :key="place.name" class="drawer-info-card drawer-item-card">
              <div class="flex gap-3">
                <img v-if="place.thumbnail" :src="place.thumbnail" :alt="place.name" class="drawer-item-thumbnail" @click="openImagesModal(place.name, place.slug)" title="Click to view images">
                <div class="flex-1">
                  <h4 class="drawer-item-title">{{ place.name }}</h4>
                  <p>{{ place.description }}</p>
                </div>
              </div>
              <div class="drawer-action-buttons">
                <a :href="place.wikipedia_url" target="_blank" rel="noopener noreferrer" class="drawer-action-btn-wiki" aria-label="Wikipedia">
                  <i class="fab fa-wikipedia-w"></i>
                </a>
                <button @click="openImagesModal(place.name, place.slug)" class="drawer-action-btn-primary">
                  <i class="fas fa-camera"></i>
                  <span>Images</span>
                </button>
                <button @click="openMoreInfoModal(place.name, place.slug)" class="drawer-action-btn-secondary">
                  <i class="fas fa-info-circle"></i>
                  <span>More</span>
                </button>
              </div>
            </div>
          </div>
          <!-- Native Species -->
          <div v-if="content.native_species && content.native_species.length" class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-paw"></i>
              <span>Native Species</span>
            </h3>
            <div v-for="animal in filteredNativeSpecies" :key="animal.name" class="drawer-info-card drawer-item-card">
              <h4 class="drawer-item-title">{{ animal.name }}</h4>
              <p>{{ animal.description }}</p>
              <div class="drawer-action-buttons">
                <a :href="animal.wikipedia_url" target="_blank" rel="noopener noreferrer" class="drawer-action-btn-wiki" aria-label="Wikipedia">
                  <i class="fab fa-wikipedia-w"></i>
                </a>
                <button @click="openImagesModal(animal.name, animal.slug)" class="drawer-action-btn-primary">
                  <i class="fas fa-camera"></i>
                  <span>Images</span>
                </button>
                <button @click="openMoreInfoModal('Animal ' + animal.name, animal.slug)" class="drawer-action-btn-secondary">
                  <i class="fas fa-info-circle"></i>
                  <span>More</span>
                </button>
              </div>
            </div>
          </div>
          <!-- Traditional Foods -->
          <div v-if="content.traditional_foods && content.traditional_foods.length" class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-utensils"></i>
              <span>Traditional Foods</span>
            </h3>
            <div v-for="food in filteredTraditionalFoods" :key="food.name" class="drawer-info-card drawer-item-card">
              <h4 class="drawer-item-title">{{ food.name }}</h4>
              <p>{{ food.description }}</p>
              <div class="drawer-action-buttons">
                <a :href="food.wikipedia_url" target="_blank" rel="noopener noreferrer" class="drawer-action-btn-wiki" aria-label="Wikipedia">
                  <i class="fab fa-wikipedia-w"></i>
                </a>
                <button @click="openImagesModal(food.name, food.slug)" class="drawer-action-btn-primary">
                  <i class="fas fa-camera"></i>
                  <span>Images</span>
                </button>
                <button @click="openMoreInfoModal('Food ' + food.name, food.slug)" class="drawer-action-btn-secondary">
                  <i class="fas fa-info-circle"></i>
                  <span>More</span>
                </button>
              </div>
            </div>
          </div>
          <!-- Famous People -->
          <div v-if="content.famous_people && content.famous_people.length" class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-users"></i>
              <span>Famous People</span>
            </h3>
            <div v-for="person in filteredFamousPeople" :key="person.name" class="drawer-info-card drawer-item-card">
              <h4 class="drawer-item-title">{{ person.name }}</h4>
              <p>{{ person.description }}</p>
              <div class="drawer-action-buttons">
                <a :href="person.wikipedia_url" target="_blank" rel="noopener noreferrer" class="drawer-action-btn-wiki" aria-label="Wikipedia">
                  <i class="fab fa-wikipedia-w"></i>
                </a>
                <button @click="openImagesModal(person.name, person.slug)" class="drawer-action-btn-primary">
                  <i class="fas fa-camera"></i>
                  <span>Images</span>
                </button>
                <button @click="openMoreInfoModal(person.name, person.slug)" class="drawer-action-btn-secondary">
                  <i class="fas fa-info-circle"></i>
                  <span>More</span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Languages Mobile -->
          <div v-if="content.languages_spoken && content.languages_spoken.length" class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-language"></i>
              <span>Languages Spoken</span>
            </h3>
            <div v-for="language in content.languages_spoken" :key="language.iso_639_1" class="drawer-info-card drawer-item-card">
              <div class="drawer-language-header">
                <div>
                  <h4 class="drawer-item-title">{{ language.name }}</h4>
                  <p v-if="language.native_name && language.native_name !== language.name" class="drawer-language-native">{{ language.native_name }}</p>
                </div>
                <span class="drawer-language-status" :class="language.official_status">
                  {{ language.official_status }}
                </span>
              </div>
              <div class="drawer-language-details">
                <span class="drawer-iso-code">{{ language.iso_639_1 }}</span>
                <span v-if="language.approx_percentage" class="drawer-percentage">{{ language.approx_percentage }}</span>
                <a v-if="language.wikipedia_url" :href="language.wikipedia_url" target="_blank" rel="noopener noreferrer" class="drawer-action-btn-wiki-sm">
                  <i class="fab fa-wikipedia-w"></i>
                </a>
              </div>
            </div>
          </div>
          
          <!-- Important Dates Mobile -->
          <div v-if="content.important_dates && content.important_dates.length" class="drawer-section">
            <h3 class="drawer-section-header">
              <i class="fas fa-calendar-alt"></i>
              <span>Important Dates</span>
            </h3>
            <div v-for="date in content.important_dates" :key="date.month_day + date.name" class="drawer-info-card drawer-item-card">
              <div class="drawer-date-header">
                <div class="drawer-date-badge">
                  <span class="drawer-date-text">{{ formatDateBadge(date.month_day) }}</span>
                </div>
                <div class="flex-1">
                  <div class="drawer-date-title-row">
                    <h4 class="drawer-item-title">{{ date.name }}</h4>
                    <span class="drawer-scope-badge" :class="date.scope">
                      {{ date.scope }}
                    </span>
                  </div>
                  <p class="drawer-date-description">{{ date.description }}</p>
                  <div class="drawer-date-details">
                    <span v-if="date.first_year" class="drawer-date-year">Since {{ date.first_year }}</span>
                    <a v-if="date.wikipedia_url" :href="date.wikipedia_url" target="_blank" rel="noopener noreferrer" class="drawer-action-btn-wiki-sm">
                      <i class="fab fa-wikipedia-w"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StarButton from './StarButton.vue';

export default {
  name: 'ModalWindow',
  components: {
    StarButton
  },
  emits: {
    'close': () => true
  },
  data() {
    return {
      isMobile: window.innerWidth <= 768,
      drawerMinimized: false,
      showShareCard: false,
      urlCopied: false,
    };
  },
  props: {
    content: {
      type: Object,
      required: true
    },
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    flagUrl() {
      if (!this.content || !this.content.country_code) {
        return ''; // Return empty string if country_code is undefined
      }
      return `https://flagpedia.net/data/flags/w580/${this.content.country_code.toLowerCase()}.webp?v=un`;
    },
    filteredLandmarks() {
      return (this.content.landmarks || []).filter(item => item && item.name);
    },
    filteredFamousPlaces() {
      return (this.content.famous_places || []).filter(item => item && item.name);
    },
    filteredNativeSpecies() {
      return (this.content.native_species || []).filter(item => item && item.name);
    },
    filteredTraditionalFoods() {
      return (this.content.traditional_foods || []).filter(item => item && item.name);
    },
    filteredFamousPeople() {
      return (this.content.famous_people || []).filter(item => item && item.name);
    },
    shareUrl() {
      if (this.content && this.content.slug) {
        return `${window.location.origin}/location/${this.content.slug}`;
      }
      return '';
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    closeModal() {
      console.log('closing main modal');
      this.$emit('close');
      document.removeEventListener('keydown', this.handleKeydown);
    },
    toggleDrawer() {
      this.drawerMinimized = !this.drawerMinimized;
    },
    minimizeDrawer() {
      this.drawerMinimized = true;
    },
    expandDrawer() {
      this.drawerMinimized = false;
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeModal(); // Close modal when Escape is pressed
      }
      console.log('handleKeydown', event);
    },
    openImagesModal(placeName, itemSlug) {
      this.$store.dispatch('images/openModal', {
        placeName: placeName,
        country: this.content.country,
        itemSlug: itemSlug
      });
    },
    openMoreInfoModal(placeName, itemSlug) {
      this.$store.dispatch('moreInfo/openModal', {
        placeName: placeName,
        country: this.content.country,
        placeSlug: this.content.slug,
        itemSlug: itemSlug
      });
    },
    formatDateBadge(monthDay) {
      if (!monthDay) return ''
      const [month, day] = monthDay.split('-')
      const date = new Date(2000, parseInt(month) - 1, parseInt(day))
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    sharePlace() {
      if (this.isMobile) {
        this.shareOnMobile();
      } else {
        this.showShareCard = true;
      }
    },
    async shareOnMobile() {
      const shareData = {
        title: `${this.content.name} - Captain Leo Maps`,
        text: `Check out this amazing place: ${this.content.name}`,
        url: this.shareUrl
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
        } else {
          // Fallback for browsers that don't support Web Share API
          this.copyUrl();
        }
      } catch (error) {
        console.log('Error sharing:', error);
        // Fallback to copy URL
        this.copyUrl();
      }
    },
    closeShareCard() {
      this.showShareCard = false;
      this.urlCopied = false;
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.urlCopied = true;
        setTimeout(() => {
          this.urlCopied = false;
          if (!this.isMobile) {
            this.showShareCard = false;
          }
        }, 2000);
      } catch (error) {
        console.log('Failed to copy URL:', error);
        // Fallback for older browsers
        if (this.$refs.shareUrlInput) {
          this.$refs.shareUrlInput.select();
          document.execCommand('copy');
          this.urlCopied = true;
          setTimeout(() => {
            this.urlCopied = false;
            if (!this.isMobile) {
              this.showShareCard = false;
            }
          }, 2000);
        }
      }
    },
    goToQuiz() {
      // Navigate to quiz page for this place
      // The quiz will be identified by the place slug
      if (this.content && this.content.slug) {
        this.$router.push({
          name: 'Quiz',
          params: {
            id: `place-${this.content.slug}`
          }
        });
      }
    },
  },
  watch: {
    // Reset drawer state when new content is received
    content: {
      handler(newContent, oldContent) {
        // If we receive new content while drawer is minimized, expand it
        if (newContent && oldContent && newContent !== oldContent) {
          this.drawerMinimized = false;
        }
      },
      deep: true
    },
    
    // Reset drawer state when modal is opened
    isOpen(newVal) {
      if (newVal) {
        this.drawerMinimized = false;
        document.addEventListener('keydown', this.handleKeydown);
      } else {
        document.removeEventListener('keydown', this.handleKeydown);
      }
    }
  }
};
</script>

<style scoped>
@import '~@fortawesome/fontawesome-free/css/all.css';
/* Optional custom styles for carousel arrows */
.carousel-arrow {
  width: 40px;
  height: 40px;
}
.carousel-arrow:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

@import '~@fortawesome/fontawesome-free/css/all.css';

/* Loader Styles */
.loader {
  border: 16px solid #f3f3f3; /* Light grey */
  border-top: 16px solid #3498db; /* Blue */
  border-radius: 50%;
  width: 120px;
  height: 120px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Info Card Styling */
.info-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  transition: all 0.3s ease;
}

.info-card:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

/* Modal Header Styling */
.modal-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
}

.flag-section {
  flex-shrink: 0;
}

.header-flag {
  width: 80px;
  height: auto;
  max-height: 60px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.8);
}

.place-info {
  flex: 1;
  min-width: 0;
}

.place-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.place-location {
  font-size: 16px;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.place-location i {
  color: #667eea;
  font-size: 14px;
}

.description-section {
  border-top: 1px solid #e2e8f0;
  padding-top: 16px;
}

.place-description {
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  font-style: italic;
}

/* Section Headers */
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
}

.section-header i {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
}

.section-header span {
  font-weight: 600;
}

/* Enhanced Action Buttons */
.action-btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border: none;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.25);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  min-height: 36px;
  white-space: nowrap;
}

.action-btn-primary:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.35);
}

.action-btn-secondary {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white !important;
  border: none;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(72, 187, 120, 0.25);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  min-height: 36px;
  white-space: nowrap;
}

.action-btn-secondary:hover {
  background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.35);
}

.action-btn-wiki {
  background: white;
  color: #64748b;
  border: 2px solid #e2e8f0;
  font-weight: 500;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.action-btn-wiki:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #475569;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Mobile responsive button styles */
@media (max-width: 768px) {
  .action-btn-primary,
  .action-btn-secondary {
    padding: 8px 12px;
    font-size: 12px;
    min-height: 32px;
    gap: 4px;
  }
  
  .action-btn-wiki {
    min-width: 32px;
    height: 32px;
    padding: 6px;
  }
  
  /* Stack buttons vertically on very small screens */
  @media (max-width: 480px) {
    .info-card .flex {
      flex-direction: column;
      gap: 8px;
    }
    
    .action-btn-primary,
    .action-btn-secondary,
    .action-btn-wiki {
      width: 100%;
      justify-content: center;
    }
  }
}

/* Modal specific styles for better positioning and accessibility */
.modal-content {
  position: relative;
  z-index: 1;
  transform: translateZ(0); /* Force hardware acceleration */
}

/* Mobile specific modal improvements */
@media (max-width: 768px) {
  .modal-content {
    margin: 80px 16px 16px 16px; /* Add top margin to avoid search bar overlap */
    width: calc(100% - 32px);
    max-height: calc(85vh - 64px); /* Reduce height to account for top margin */
  }
  
  /* Ensure close button is always accessible on mobile */
  .modal-content button[aria-label="Close Modal"] {
    position: sticky;
    top: 8px;
    right: 8px;
    z-index: 10;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
}

/* Gradient Close Button */
.gradient-close-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.gradient-close-btn:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* Mobile Drawer Styles */
.mobile-drawer-overlay {
  position: fixed;
  bottom: 20vh; /* Position above regular mobile footer menu */
  left: 0;
  right: 0;
  z-index: 999999;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Small mobile adjustment for smaller footer */
@media (max-width: 480px) {
  .mobile-drawer-overlay {
    bottom: 12vh; /* Position above small mobile footer menu */
  }
}

/* Minimized Header Bar */
.drawer-header-bar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  padding: 12px 20px;
  cursor: pointer;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 20px 20px 0 0;
}

.drawer-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.drawer-mini-flag {
  width: 32px;
  height: auto;
  max-height: 24px;
  object-fit: contain;
  border-radius: 4px;
}

.drawer-mini-info {
  flex: 1;
  min-width: 0;
}

.drawer-mini-title {
  font-size: 16px;
  font-weight: 600;
  color: white !important;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-mini-location {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-expand-icon {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
}

/* Full Drawer Content */
.drawer-content {
  background: white;
  max-height: 65vh; /* Reduced height for regular mobile (85vh - 20vh) */
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Small mobile gets more height due to smaller footer */
@media (max-width: 480px) {
  .drawer-content {
    max-height: 73vh; /* More height for small mobile (85vh - 12vh) */
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Drawer Header */
.drawer-header {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border-radius: 20px 20px 0 0;
  z-index: 10;
}

.drawer-controls {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px 0 20px;
}

.drawer-minimize-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white !important;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.drawer-minimize-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.drawer-close-btn {
  width: 36px !important;
  height: 36px !important;
}

.drawer-modal-header {
  padding: 0 20px 20px 20px;
}

.drawer-header-content-full {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.drawer-flag-section {
  flex-shrink: 0;
}

.drawer-header-flag {
  width: 48px;
  height: auto;
  max-height: 36px;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.drawer-place-info {
  flex: 1;
  min-width: 0;
}

.drawer-place-title {
  font-size: 20px;
  font-weight: 700;
  color: white !important;
  margin: 0 0 4px 0;
}

.drawer-place-location {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}


/* Scrollable Content */
.drawer-scrollable-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.drawer-content-grid {
  padding: 20px;
}

.drawer-section {
  margin-bottom: 24px;
}

.drawer-description-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.drawer-description-card .drawer-place-description {
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  font-style: italic;
  text-align: center;
}

.drawer-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.25);
}

.drawer-section-header i {
  font-size: 16px;
}

.drawer-info-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.3s ease;
}

.drawer-info-card:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.drawer-info-card p {
  color: #4b5563;
  line-height: 1.6;
  margin: 0;
  font-size: 14px;
}

.drawer-item-card {
  margin-bottom: 16px;
}

.drawer-item-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Action Buttons */
.drawer-action-buttons {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.drawer-action-btn-primary,
.drawer-action-btn-secondary,
.drawer-action-btn-wiki {
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  border: none;
}

.drawer-action-btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.25);
}

.drawer-action-btn-primary:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.35);
}

.drawer-action-btn-secondary {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white !important;
  box-shadow: 0 2px 4px rgba(72, 187, 120, 0.25);
}

.drawer-action-btn-secondary:hover {
  background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(72, 187, 120, 0.35);
}

.drawer-action-btn-wiki {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding: 8px;
  min-width: 32px;
  justify-content: center;
}

.drawer-action-btn-wiki:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #475569;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.drawer-action-btn-wiki i {
  font-size: 14px;
}

/* Drawer animations */
.drawer-minimized .drawer-content {
  animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideDown {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
}

/* Desktop Modal New Sections */
.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.stat-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.stat-icon {
  color: #667eea;
  font-size: 18px;
  width: 24px;
  text-align: center;
  flex-shrink: 0;
}

.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  line-height: 1;
  margin-bottom: 2px;
}

.stat-value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
  line-height: 1.2;
}

/* Language Status Badges */
.language-status-badge {
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid;
}

.language-status-badge.official {
  background: #dcfce7;
  color: #166534;
  border-color: #bbf7d0;
}

.language-status-badge.co-official {
  background: #dbeafe;
  color: #1e40af;
  border-color: #bfdbfe;
}

.language-status-badge.regional {
  background: #fef3c7;
  color: #92400e;
  border-color: #fed7aa;
}

.language-status-badge.minority {
  background: #f3f4f6;
  color: #4b5563;
  border-color: #d1d5db;
}

.iso-code {
  background: #f1f5f9;
  color: #64748b;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.percentage {
  color: #667eea;
  font-weight: 600;
  font-size: 12px;
}

.action-btn-wiki-sm {
  background: #f59e0b;
  color: white !important;
  border: none;
  border-radius: 4px;
  padding: 4px 6px;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-btn-wiki-sm:hover {
  background: #d97706;
  transform: scale(1.05);
}

/* Important Dates */
.date-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
  min-width: 60px;
  flex-shrink: 0;
}

.date-text {
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.scope-badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.scope-badge.national {
  background: #dcfce7;
  color: #166534;
}

.scope-badge.regional {
  background: #fef3c7;
  color: #92400e;
}

.scope-badge.city {
  background: #dbeafe;
  color: #1e40af;
}

/* Mobile Drawer New Sections */
.drawer-stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
}

.drawer-stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.drawer-stat-icon {
  color: #667eea;
  font-size: 14px;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.drawer-stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.drawer-stat-label {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  line-height: 1;
}

.drawer-stat-value {
  font-size: 11px;
  color: #1e293b;
  font-weight: 600;
  line-height: 1.2;
  margin-top: 1px;
}

/* Mobile Language Styles */
.drawer-language-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.drawer-language-native {
  font-size: 12px;
  color: #64748b;
  font-style: italic;
  margin-top: 2px;
}

.drawer-language-status {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.drawer-language-status.official {
  background: #dcfce7;
  color: #166534;
}

.drawer-language-status.co-official {
  background: #dbeafe;
  color: #1e40af;
}

.drawer-language-status.regional {
  background: #fef3c7;
  color: #92400e;
}

.drawer-language-status.minority {
  background: #f3f4f6;
  color: #4b5563;
}

.drawer-language-details {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drawer-iso-code {
  background: #f1f5f9;
  color: #64748b;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
}

.drawer-percentage {
  color: #667eea;
  font-weight: 600;
  font-size: 10px;
}

.drawer-action-btn-wiki-sm {
  background: #f59e0b;
  color: white !important;
  border: none;
  border-radius: 3px;
  padding: 3px 5px;
  text-decoration: none;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
}

/* Mobile Date Styles */
.drawer-date-header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.drawer-date-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100());
  color: white !important;
  border-radius: 6px;
  padding: 6px;
  text-align: center;
  min-width: 50px;
  flex-shrink: 0;
}

.drawer-date-text {
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  color: white !important;
}

/* Override for mobile drawer date badge to ensure visibility */
.drawer-info-card .drawer-date-badge {
  background: #4a5568 !important;
  color: white !important;
}

.drawer-info-card .drawer-date-text {
  color: white !important;
}

.drawer-date-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
}

.drawer-scope-badge {
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.drawer-scope-badge.national {
  background: #dcfce7;
  color: #166534;
}

.drawer-scope-badge.regional {
  background: #fef3c7;
  color: #92400e;
}

.drawer-scope-badge.city {
  background: #dbeafe;
  color: #1e40af;
}

.drawer-date-description {
  font-size: 12px;
  color: #4b5563;
  line-height: 1.4;
  margin-bottom: 6px;
}

.drawer-date-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.drawer-date-year {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
}

/* Language status indicators for compact view */
.language-status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-left: 4px;
}
.language-status-indicator.official {
  background-color: #22c55e;
}
.language-status-indicator.co-official {
  background-color: #3b82f6;
}
.language-status-indicator.regional {
  background-color: #f59e0b;
}
.language-status-indicator.minority {
  background-color: #9ca3af;
}

/* Quiz Button Styles */
.gradient-quiz-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.gradient-quiz-btn:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4);
}

/* Share Button Styles */
.gradient-share-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.gradient-share-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

/* Desktop Modal Share Card */
.modal-share-card {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-share-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 600;
  color: #166534;
}

.modal-share-card-header i {
  color: #059669;
}

.modal-share-close-btn {
  margin-left: auto;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.modal-share-close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #374151;
}

.modal-share-url-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.modal-share-url-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  color: #374151;
  min-width: 0;
}

.modal-share-url-input:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.modal-copy-btn {
  padding: 12px 20px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white !important;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  min-width: 100px;
  justify-content: center;
}

.modal-copy-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #065f46 100%);
  transform: translateY(-1px);
}

.modal-copy-btn.copied {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.modal-copy-btn.copied:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

/* Mobile Drawer Action Buttons */
.drawer-right-controls {
  display: flex;
  gap: 8px;
}

.drawer-quiz-btn {
  width: 36px !important;
  height: 36px !important;
}

.drawer-share-btn {
  width: 36px !important;
  height: 36px !important;
}

.drawer-close-btn {
  width: 36px !important;
  height: 36px !important;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .modal-share-card {
    margin: 0 -4px 16px -4px;
    padding: 14px;
  }
  
  .modal-share-url-container {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .modal-copy-btn {
    width: 100%;
    justify-content: center;
    padding: 14px;
  }
}

/* Thumbnail styles */
.item-thumbnail {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
}

.item-thumbnail:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-color: #667eea;
}

.drawer-item-thumbnail {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
}

.drawer-item-thumbnail:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-color: #667eea;
}

@media (max-width: 768px) {
  .item-thumbnail {
    width: 64px;
    height: 64px;
  }
  
  .drawer-item-thumbnail {
    width: 48px;
    height: 48px;
  }
}

</style>
